/**
 * CORE — the deterministic fixed-timestep marble integrator.
 *
 * WHY PURE JS, NOT THE RAPIER SOLVER, FOR SCORING
 * ------------------------------------------------
 * The leaderboard requires bit-identical results across the browser and the
 * Cloudflare Worker. Rapier's constraint solver runs in WebAssembly and, while
 * deterministic on a single platform, is NOT guaranteed bit-equal across
 * different wasm runtimes / CPUs (fused-multiply-add, SIMD, rounding of
 * transcendental ops can differ). Floating-point *doubles in plain JS* ARE
 * specified by IEEE-754 and evaluate identically on V8 (browser), Node, and the
 * Workers runtime. So the score-determining dynamics live here, in pure JS:
 * semi-implicit Euler + analytic sphere-vs-OBB response against the explicit
 * CourseSpec geometry. The live sim is RNG-free, as the contract demands.
 *
 * Rapier is still initialized by the Simulation wrapper (the dependency and its
 * async wasm init are part of the locked surface), and remains available for any
 * future use, but it does not influence the deterministic state hash.
 *
 * Owned by: Core. Internal engine consumed by the Simulation.
 */

import { FIXED_DT } from '@/types';
import type {
  CourseSpec,
  CourseElement,
  CourseCheckpoint,
  Vec3,
  Quat,
  RunStatus,
} from '@/types';
import {
  add,
  addScaled,
  scale,
  dot,
  normalize,
  length,
  v3,
  IDENTITY_QUAT,
} from './vecmath';
import {
  sphereVsOBB,
  sphereOverlapsOBB,
  sphereOverlapsSphere,
  resolveVelocity,
} from './collision';

/** A solid element flattened for fast collision iteration. */
interface SolidBody {
  readonly element: CourseElement;
  /** Current world center. Mutable because movers shift it each tick. */
  center: Vec3;
  readonly rotation: Quat;
  readonly halfExtents: Vec3;
  readonly friction: number;
  readonly restitution: number;
  /** Movers shift their center each tick; this is the base (tick-0) center. */
  readonly baseCenter: Vec3;
  readonly isMover: boolean;
}

export interface PhysicsState {
  position: Vec3;
  velocity: Vec3;
  rotation: Quat;
  /** Angular velocity used purely for visual roll (does not affect scoring path
   *  beyond being derived from linear motion). */
  angvel: Vec3;
  /** Index of the NEXT checkpoint to reach. */
  checkpointIndex: number;
  /** World position of the last reached checkpoint (respawn anchor). */
  lastCheckpoint: Vec3;
  status: RunStatus;
  tick: number;
  /** Accumulated path distance along the ground (for scoring + progress). */
  distance: number;
}

/** Per-second tangential resistance for a grounded (rolling) marble, scaled by
 *  the contact surface friction. Tuned so the marble reaches a good fraction of
 *  maxSpeed rather than sticking to the floor. Kept low so the marble carries
 *  forward momentum down the corridor rather than dragging on the floor. */
const ROLL_RESISTANCE = 0.6;

/** LANE-SHIFTER MODEL — DIRECT-LATERAL steering (a lane-changer, not a wheel).
 *
 *  The marble auto-rolls forward along WORLD +Z; the player steers ONLY the
 *  lateral (X) velocity directly. Each tick:
 *    1. AUTO-FORWARD: a constant `FORWARD_ACCEL` thrust along world +Z (plus
 *       gravity), perpetual, bounded by the progress-scaled cap — a momentum
 *       roller that keeps building forward speed.
 *    2. DIRECT LATERAL: steerX selects a TARGET lateral velocity
 *       `targetVx = -steerX * MAX_LATERAL_SPEED`, and the current X-velocity is
 *       eased toward it by `LATERAL_RESPONSE` each tick. There is no heading and no
 *       carving — the player simply slides the marble across lanes. This is what
 *       keeps the model stable: the lateral command is a pure function of (current
 *       X-vel, steerX), so there is no heading/velocity feedback whip and no
 *       oversteer slamming into walls.
 *
 *  SIGN CONVENTION: the forward-locked chase camera looks down +Z, which mirrors
 *  world X on screen, so steerX > 0 (swipe RIGHT) must drive toward world -X to
 *  move RIGHT on screen — hence the negation in targetVx.
 *
 *  `_steerZ` is ignored (no gas/brake — forward is automatic).
 *
 *  Determinism: the lateral update is a PURE function of (current X-vel, steerX);
 *  every op is IEEE-754 double arithmetic, no RNG, no platform globals. */

/** Constant forward acceleration (units/s²) applied along WORLD +Z every tick.
 *  Perpetual auto-forward: the marble keeps building forward speed, bounded by the
 *  progress-scaled cap (PROGRESS_SPEED_*). */
const FORWARD_ACCEL = 24.0;

/** Top lateral (X) speed (world units/s) at full steer (|steerX| = 1). The target
 *  lateral velocity is -steerX * MAX_LATERAL_SPEED. Bumped to 26 (from a slightly
 *  sluggish lower value) so dodging across lanes feels responsive without
 *  oversteering past a gap. */
const MAX_LATERAL_SPEED = 26.0;

/** Per-tick easing fraction by which the marble's X-velocity moves toward the
 *  steer-selected target lateral velocity. 0.3/tick gives a quick-but-smooth lane
 *  change (reaches most of the target in a few ticks) without snapping instantly,
 *  so inputs read as a crisp slide rather than a teleport. */
const LATERAL_RESPONSE = 0.3;

/** Momentum: the effective speed cap grows with distance rolled, so a clean run
 *  keeps accelerating (it's a momentum roller). The cap is
 *  maxSpeed * (1 + min(distance * GAIN, MAX)).
 *
 *  Tuned for the LONG escalating course: the cap should keep climbing across the
 *  whole multi-minute run instead of saturating in the first few seconds.
 *  PROGRESS_SPEED_MAX is raised to 2.0 (top speed up to 3× base) and the GAIN is
 *  lowered so the cap ramps gradually over the long-course distance — the run
 *  gets faster and harder the further you survive. With GAIN=0.0009 the cap is
 *  still climbing past ~2200 units of travel (a long survival distance), which is
 *  where the hardest, fastest sections live. */
const PROGRESS_SPEED_GAIN = 0.0009;
const PROGRESS_SPEED_MAX = 2.0;

/** LAUNCH-RAMP loft cap (world units/s). Deliberate launch ramps (see
 *  generate.ts: `emitLaunchRamp`) redirect part of the marble's forward speed
 *  UPWARD off a discrete up-wedge so the player can jump a gap. Without a cap the
 *  loft scales with the (progress-grown) forward speed, so a fast late-course
 *  marble would be flung absurdly high and far — an uncontrolled fling that lands
 *  unpredictably (possibly in a later hole). We clamp ONLY the UPWARD (positive-Y)
 *  velocity to this value, which bounds the air TIME (≈ 2*VY/|gravityY| ≈ 0.9s)
 *  and therefore the jump's RANGE, regardless of how fast the marble is going.
 *  The result is a consistent, predictable hop: the tuned wedge + this cap make
 *  the marble clear the launch gap and land on the long flat run-out ahead every
 *  time. Gravity is untouched (the marble still falls normally), downward velocity
 *  is never clamped, and grounded motion is unaffected (a grounded marble's |vY|
 *  stays well under this). Pure, deterministic per-tick clamp.
 *
 *  RAISED 8.5 -> 11.5 after the speed fix. Removing the `slow` hazards let the
 *  marble actually CRUISE (tens of u/s), and at that speed an 8.5 cap produced a
 *  weak ~0.9s hop that read as "riding the surface up" rather than a launch. At
 *  11.5 the air time is ≈ 2*11.5/18 ≈ 1.28s: the ball visibly LEAVES the wedge in a
 *  clear arc, clears the gap, and re-grounds on the 60-unit flat run-out. It stays
 *  CAPPED (predictable/controllable, not a wild fling) — the loft no longer scales
 *  with the progress-grown forward speed, so a fast late marble jumps the same
 *  height as an early one; only its horizontal range varies, which the long landing
 *  run still contains (≈ 1.28s * ~55 u/s ≈ 70 units < wedge+gap+60-unit run-out). */
const MAX_LAUNCH_VY = 11.5;

/** ANTI-TUNNELING — the largest translation (world units) the marble is allowed
 *  to advance before we re-run collision. At high speed the per-tick move exceeds
 *  a wall's thickness, so a single discrete sphere-vs-OBB test lets the marble
 *  pass clean through. We split each tick's translation into N sub-steps of at
 *  most this length (N = max(1, ceil(moveDistance / MAX_SUBSTEP))) and resolve
 *  collisions at EACH sub-step, so the marble can never skip a wall/gap in one
 *  tick. Set near the marble radius (0.5) so a sub-step can't straddle a wall.
 *  Pure constant — substepping stays a deterministic function of state. */
const MAX_SUBSTEP = 0.5;

export class PhysicsWorld {
  readonly spec: CourseSpec;
  private readonly solids: SolidBody[] = [];
  private readonly movers: SolidBody[] = [];
  private readonly radius: number;
  private readonly gravity: Vec3;
  private readonly linearDamping: number;
  private readonly maxSpeed: number;
  private readonly marbleFriction: number;
  private readonly marbleRestitution: number;
  /** A kill plane: anything below this Y is out of bounds. */
  private readonly killY: number;

  state: PhysicsState;

  constructor(spec: CourseSpec) {
    this.spec = spec;
    const t = spec.tuning;
    this.radius = t.marbleRadius;
    this.gravity = t.gravity;
    this.linearDamping = t.linearDamping;
    this.maxSpeed = t.maxSpeed;
    this.marbleFriction = t.marbleFriction;
    this.marbleRestitution = t.marbleRestitution;
    this.killY = spec.bounds.min[1] - 2;

    for (const el of spec.elements) {
      if (el.kind === 'track' || el.kind === 'wall' || el.kind === 'ramp') {
        this.solids.push(this.toSolid(el, el.surface.friction, el.surface.restitution, false));
      } else if (el.kind === 'mover') {
        const m = this.toSolid(el, el.surface.friction, el.surface.restitution, true);
        this.solids.push(m);
        this.movers.push(m);
      }
      // booster + hazard are triggers, handled separately (not solids).
    }

    this.state = this.initialState();
  }

  private toSolid(
    el: CourseElement,
    friction: number,
    restitution: number,
    isMover: boolean,
  ): SolidBody {
    return {
      element: el,
      center: el.transform.position,
      rotation: el.transform.rotation,
      halfExtents: el.halfExtents,
      friction,
      restitution,
      baseCenter: el.transform.position,
      isMover,
    };
  }

  private initialState(): PhysicsState {
    const spawn = this.spec.spawn.position;
    return {
      position: [spawn[0], spawn[1], spawn[2]],
      velocity: [0, 0, 0],
      rotation: this.spec.spawn.rotation,
      angvel: [0, 0, 0],
      checkpointIndex: 0,
      lastCheckpoint: [spawn[0], spawn[1], spawn[2]],
      status: 'running',
      tick: 0,
      distance: 0,
    };
  }

  reset(): void {
    this.state = this.initialState();
    // Restore movers to their tick-0 pose.
    for (const m of this.movers) {
      m.center = m.baseCenter;
    }
  }

  /** Deterministic mover pose at a given tick:
   *  center = base + axis * amplitude * sin(2π * (tick + phase) / period). */
  private updateMovers(tick: number): void {
    for (const m of this.movers) {
      if (m.element.kind !== 'mover') continue;
      const motion = m.element.motion;
      const theta = (2 * Math.PI * (tick + motion.phaseTicks)) / motion.periodTicks;
      const offset = Math.sin(theta) * motion.amplitude;
      m.center = addScaled(m.baseCenter, motion.axis, offset);
    }
  }

  /**
   * Advance the world by exactly one fixed tick using the given quantized steer
   * (already mapped back to raw [-1, 1] by the Simulation). Mutates `state` and
   * returns it. Pure function of (state, course, steer) — no randomness.
   */
  step(steerX: number, _steerZ: number): PhysicsState {
    const s = this.state;
    if (s.status !== 'running') return s;

    const dt = FIXED_DT;
    s.tick += 1;
    this.updateMovers(s.tick);

    // --- Forces -> acceleration (semi-implicit Euler) ---
    // Gravity (vertical) plus PERPETUAL auto-forward thrust along WORLD +Z. The
    // marble keeps building forward speed (bounded by the progress-scaled cap).
    // FORWARD_ACCEL is one-directional along +Z — there is no reverse and no
    // gas/brake (_steerZ is intentionally IGNORED).
    let accel: Vec3 = [this.gravity[0], this.gravity[1], this.gravity[2] + FORWARD_ACCEL];
    let vel = addScaled(s.velocity, accel, dt);

    // --- DIRECT-LATERAL steering (lane-shifter) ---
    // steerX selects a TARGET lateral (X) velocity; ease the current X-velocity
    // toward it by LATERAL_RESPONSE. NOTE the negation: the forward-locked +Z
    // chase camera mirrors world X on screen, so steerX > 0 (swipe RIGHT) drives
    // toward world -X to move RIGHT on screen. Pure function of (current X-vel,
    // steerX): deterministic, no heading, no oversteer.
    const targetVx = -steerX * MAX_LATERAL_SPEED;
    vel = [vel[0] + (targetVx - vel[0]) * LATERAL_RESPONSE, vel[1], vel[2]];

    // Linear damping (exponential, frame-rate independent at fixed dt).
    const damp = 1 - this.linearDamping * dt;
    vel = scale(vel, damp);

    // Booster pads: a sustained impulse while overlapping (sampled at the
    // pre-collision position, which is where the player engaged the pad).
    this.applyBoosters(s.position, dt, (boost) => {
      vel = add(vel, boost);
    });

    // Momentum: the effective speed cap grows with distance rolled, so a clean
    // run keeps accelerating instead of pinning at a flat maxSpeed. Still capped
    // (well-conditioned sim) by the bounded progress term.
    const effMaxSpeed =
      this.maxSpeed * (1 + Math.min(s.distance * PROGRESS_SPEED_GAIN, PROGRESS_SPEED_MAX));
    const speedSq = vel[0] * vel[0] + vel[1] * vel[1] + vel[2] * vel[2];
    if (speedSq > effMaxSpeed * effMaxSpeed && speedSq > 0) {
      const sc = effMaxSpeed / Math.sqrt(speedSq);
      vel = [vel[0] * sc, vel[1] * sc, vel[2] * sc];
    }

    // --- Integrate position with ANTI-TUNNELING SUBSTEPPING ---
    // At high speed (especially after the progress-scaled cap grows) the per-tick
    // translation |vel|*dt can exceed a wall's thickness, so a single discrete
    // sphere-vs-OBB test at the end position would let the marble pass clean
    // through a wall or a joint. Split the translation into N equal sub-steps of
    // at most MAX_SUBSTEP world units and run the FULL collision-resolution loop
    // at EACH sub-step, advancing from the current position. Because `vel` is
    // deflected by a collision, later sub-steps in the same tick follow the
    // corrected velocity rather than ploughing onward into the wall. This is a
    // pure deterministic function of (state, course): N depends only on |vel|*dt.
    //
    // SWEPT TRIGGERS: hazards, checkpoints, and the out-of-bounds (kill-plane)
    // test are ALSO evaluated at EACH sub-step position — not once at the final
    // position. At the new high speeds the per-tick move is 2+ units, so a single
    // end-of-tick trigger test would skip clean over a thin hazard/checkpoint
    // volume (the live-playtest "red blocks no longer kill" bug). Sweeping the
    // triggers per sub-step (in order) means a KILL block reliably ends the run
    // the instant ANY sub-step overlaps it, checkpoints register as the marble
    // passes through at any speed, and a fall off the edge is caught mid-path.
    let pos: Vec3 = [s.position[0], s.position[1], s.position[2]];
    const moveDist = length(vel) * dt;
    const substeps = Math.max(1, Math.ceil(moveDist / MAX_SUBSTEP));
    const subDt = dt / substeps;

    let grounded = false;
    let groundFriction = 0;
    // Distance is accumulated per sub-step (horizontal travel of each resolved
    // slice), so the running total stays correct even when the tick is cut short
    // by a kill / reset / out-of-bounds, and so a mid-path checkpoint sees the
    // up-to-that-point total. `prev` tracks the previous sub-step's position.
    let prev: Vec3 = pos;
    for (let sub = 0; sub < substeps; sub++) {
      // Advance this sub-step's slice of the translation, then resolve.
      pos = addScaled(pos, vel, subDt);
      const res = this.resolveCollisions(pos, vel, s.tick);
      pos = res.pos;
      vel = res.vel;
      if (res.grounded) {
        grounded = true;
        if (res.groundFriction > groundFriction) groundFriction = res.groundFriction;
      }
      // LAUNCH-RAMP loft cap: a deliberate up-wedge redirects forward speed UPWARD
      // here (in the collision response). Clamp the UPWARD component so the jump's
      // loft — and thus its air time and range — stay bounded and predictable no
      // matter how fast the marble was going (see MAX_LAUNCH_VY). Only the positive
      // (up) component is capped; falling is untouched, so gravity still pulls the
      // marble down into a clean landing. Applied per sub-step so the capped
      // velocity carries into the rest of this tick's motion. Deterministic.
      if (vel[1] > MAX_LAUNCH_VY) vel = [vel[0], MAX_LAUNCH_VY, vel[2]];

      // Distance accounting for THIS sub-step's resolved slice (horizontal only).
      s.distance += Math.hypot(pos[0] - prev[0], pos[2] - prev[2]);
      prev = pos;

      // --- Hazards (triggers), swept at this sub-step position ---
      const hazardOutcome = this.evalHazards(pos);
      if (hazardOutcome === 'kill') {
        // A KILL block ends the run the instant any sub-step overlaps it. Stop
        // stepping immediately.
        s.status = 'dnf';
        this.commit(s, pos, vel);
        return s;
      } else if (hazardOutcome === 'reset') {
        // Snap to the last checkpoint and zero velocity. Stop sweeping the rest of
        // this tick: the marble has teleported, so the remaining sub-steps of the
        // old trajectory no longer apply.
        pos = [s.lastCheckpoint[0], s.lastCheckpoint[1], s.lastCheckpoint[2]];
        vel = [0, 0, 0];
        this.commit(s, pos, vel);
        return s;
      } else if (hazardOutcome === 'slow') {
        vel = scale(vel, 0.55);
      }

      // --- Out of bounds (fell off), caught mid-path ---
      if (pos[1] < this.killY) {
        s.status = 'dnf';
        this.commit(s, pos, vel);
        return s;
      }

      // --- Checkpoints (ordered gates), registered at this sub-step ---
      this.evalCheckpoints(pos, s);
      // Cast: TS narrowed s.status to 'running' from the early guard and can't see
      // that evalCheckpoints() may mutate it to 'finished'.
      if ((s.status as RunStatus) === 'finished') {
        this.commit(s, pos, vel);
        return s;
      }
    }

    // Ground rolling-resistance: a gentle per-second tangential damping applied
    // ONCE per tick (never scrubbed on every contact / sub-step), so the marble
    // carries speed down the course instead of sticking to the floor.
    if (grounded) {
      const keep = Math.max(0, 1 - groundFriction * ROLL_RESISTANCE * dt);
      vel = [vel[0] * keep, vel[1], vel[2] * keep];
    }

    this.commit(s, pos, vel);
    return s;
  }

  /**
   * Resolve the marble (sphere at `pos`, moving at `vel`) against every solid in
   * the world using a few positional-correction iterations. Returns the corrected
   * position and velocity plus whether a ground-ish contact was made (and its
   * friction). Pure deterministic function of (pos, vel, tick, world) — extracted
   * so the substepping loop in `step` can run it at each sub-step. `tick` is used
   * only to sample mover velocity (unchanged within a tick).
   */
  private resolveCollisions(
    pos: Vec3,
    vel: Vec3,
    tick: number,
  ): { pos: Vec3; vel: Vec3; grounded: boolean; groundFriction: number } {
    let grounded = false;
    let groundFriction = 0;
    for (let iter = 0; iter < 4; iter++) {
      let anyHit = false;
      for (const solid of this.solids) {
        const contact = sphereVsOBB(
          pos,
          this.radius,
          solid.center,
          solid.rotation,
          solid.halfExtents,
        );
        if (contact === null) continue;
        anyHit = true;
        // Positional correction: push the marble out along the normal.
        pos = addScaled(pos, contact.normal, contact.depth);
        // Velocity response: combine marble + surface coefficients (average).
        const restitution = (this.marbleRestitution + solid.restitution) * 0.5;
        // Movers impart their own velocity along the contact normal so they can
        // carry / nudge the marble deterministically.
        if (solid.isMover) {
          const moverVel = this.moverVelocity(solid, tick);
          vel = add(vel, scale(contact.normal, Math.max(0, dot(moverVel, contact.normal))));
        }
        vel = resolveVelocity(vel, contact.normal, restitution);
        // Track a ground-ish (upward-facing) contact; rolling resistance handled
        // once per tick by the caller.
        if (contact.normal[1] > 0.5) {
          grounded = true;
          const f = (this.marbleFriction + solid.friction) * 0.5;
          if (f > groundFriction) groundFriction = f;
        }
      }
      if (!anyHit) break;
    }
    return { pos, vel, grounded, groundFriction };
  }

  private commit(s: PhysicsState, pos: Vec3, vel: Vec3): void {
    s.position = pos;
    s.velocity = vel;
    // Derive a visual roll: spin axis = up x velocityDir, rate ∝ speed / radius.
    const speed = length(vel);
    if (speed > 1e-6) {
      const dir = normalize(vel);
      // roll axis perpendicular to motion in the ground plane
      s.angvel = scale(v3(dir[2], 0, -dir[0]), speed / this.radius);
    } else {
      s.angvel = [0, 0, 0];
    }
    s.rotation = integrateRoll(s.rotation, s.angvel, FIXED_DT);
  }

  private moverVelocity(solid: SolidBody, tick: number): Vec3 {
    if (solid.element.kind !== 'mover') return [0, 0, 0];
    const motion = solid.element.motion;
    // d/dt of amplitude*sin(2π(t+phase)/period), with t in ticks => per-tick,
    // converted to per-second by dividing by FIXED_DT.
    const w = (2 * Math.PI) / motion.periodTicks;
    const dOffsetPerTick = Math.cos(w * (tick + motion.phaseTicks)) * motion.amplitude * w;
    const perSecond = dOffsetPerTick / FIXED_DT;
    return scale(motion.axis, perSecond);
  }

  private applyBoosters(
    pos: Vec3,
    dt: number,
    apply: (boost: Vec3) => void,
  ): void {
    for (const el of this.spec.elements) {
      if (el.kind !== 'booster') continue;
      if (sphereOverlapsOBB(pos, this.radius, el.transform.position, el.transform.rotation, el.halfExtents)) {
        apply(scale(el.impulse, dt));
      }
    }
  }

  private evalHazards(pos: Vec3): 'none' | 'slow' | 'reset' | 'kill' {
    let worst: 'none' | 'slow' | 'reset' | 'kill' = 'none';
    const rank = { none: 0, slow: 1, reset: 2, kill: 3 } as const;
    for (const el of this.spec.elements) {
      if (el.kind !== 'hazard') continue;
      if (!sphereOverlapsOBB(pos, this.radius, el.transform.position, el.transform.rotation, el.halfExtents)) {
        continue;
      }
      const effect: 'slow' | 'reset' | 'kill' =
        el.effect === 'reset_to_checkpoint' ? 'reset' : el.effect === 'kill' ? 'kill' : 'slow';
      if (rank[effect] > rank[worst]) worst = effect;
    }
    return worst;
  }

  private evalCheckpoints(pos: Vec3, s: PhysicsState): void {
    const cps: readonly CourseCheckpoint[] = this.spec.checkpoints;
    while (s.checkpointIndex < cps.length) {
      const cp = cps[s.checkpointIndex];
      if (!sphereOverlapsSphere(pos, this.radius, cp.position, cp.radius)) break;
      // Reached this gate in order.
      s.lastCheckpoint = [cp.position[0], cp.position[1], cp.position[2]];
      s.checkpointIndex += 1;
      if (cp.isFinish) {
        s.status = 'finished';
        break;
      }
    }
  }
}

/** Integrate a roll quaternion by an angular velocity over dt (small-angle). */
function integrateRoll(rot: Quat, angvel: Vec3, dt: number): Quat {
  const wmag = length(angvel);
  if (wmag < 1e-9) return rot;
  const half = (wmag * dt) * 0.5;
  const s = Math.sin(half) / wmag;
  const dq: Quat = [angvel[0] * s, angvel[1] * s, angvel[2] * s, Math.cos(half)];
  // world-space spin: dq * rot
  const [ax, ay, az, aw] = dq;
  const [bx, by, bz, bw] = rot;
  const q: Quat = [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
  // normalize
  const len = Math.sqrt(q[0] * q[0] + q[1] * q[1] + q[2] * q[2] + q[3] * q[3]);
  if (len === 0) return IDENTITY_QUAT;
  const inv = 1 / len;
  return [q[0] * inv, q[1] * inv, q[2] * inv, q[3] * inv];
}
